// src/services/mailingServices.js
import axios from "axios";

const baseURL = `${process.env.REACT_APP_API_URL}/api/mailing`;

const api = axios.create({ baseURL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* ============================================================
   HELPERS
   ============================================================ */

const buildQS = (params = {}) => {
  const q = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== "") {
          q.append(key, item);
        }
      });
    } else {
      q.set(key, value);
    }
  });

  const str = q.toString();
  return str ? `?${str}` : "";
};

const safe = async (promise, fallback = null, label = "mailingAPI") => {
  try {
    const { data } = await promise;
    return data;
  } catch (err) {
    console.error(`${label} error:`, err);
    return fallback;
  }
};

/* ============================================================
   TEMPLATES D'EMAILS
   ============================================================ */

/**
 * GET /api/mailing/templates
 * params possibles : { product_key, status }
 */
export const getEmailTemplates = async (params = {}) =>
  safe(
    api.get(`/templates${buildQS(params)}`),
    [],
    "getEmailTemplates"
  );

/**
 * GET /api/mailing/templates/:id
 */
export const getEmailTemplateById = async (id) =>
  safe(
    api.get(`/templates/${id}`),
    null,
    "getEmailTemplateById"
  );

/**
 * POST /api/mailing/templates
 * payload :
 * {
 *   name,
 *   slug,
 *   product_key,
 *   subject,
 *   preheader,
 *   design_json,
 *   html,
 *   status
 * }
 */
export const createEmailTemplate = async (payload) =>
  safe(
    api.post(`/templates`, payload),
    null,
    "createEmailTemplate"
  );

/**
 * PATCH /api/mailing/templates/:id
 */
export const updateEmailTemplate = async (id, payload) =>
  safe(
    api.patch(`/templates/${id}`, payload),
    null,
    "updateEmailTemplate"
  );

/**
 * DELETE /api/mailing/templates/:id
 */
export const deleteEmailTemplate = async (id) =>
  safe(
    api.delete(`/templates/${id}`),
    null,
    "deleteEmailTemplate"
  );

/**
 * POST /api/mailing/templates/:id/duplicate
 */
export const duplicateEmailTemplate = async (id) =>
  safe(
    api.post(`/templates/${id}/duplicate`),
    null,
    "duplicateEmailTemplate"
  );

/**
 * POST /api/mailing/templates/:id/preview
 * payload : { prospection_id? }
 */
export const previewEmailTemplate = async (id, payload = {}) =>
  safe(
    api.post(`/templates/${id}/preview`, payload),
    null,
    "previewEmailTemplate"
  );

/* ============================================================
   COMPTES EXPÉDITEURS
   ============================================================ */

/**
 * GET /api/mailing/senders
 */
export const getEmailSenders = async () =>
  safe(
    api.get(`/senders`),
    [],
    "getEmailSenders"
  );

/**
 * POST /api/mailing/senders
 * payload :
 * {
 *   label,
 *   from_name,
 *   from_email,
 *   reply_to,
 *   smtp_host,
 *   smtp_port,
 *   smtp_secure,
 *   smtp_user,
 *   daily_limit,
 *   hourly_limit,
 *   is_default,
 *   is_active
 * }
 */
export const createEmailSender = async (payload) =>
  safe(
    api.post(`/senders`, payload),
    null,
    "createEmailSender"
  );

/**
 * PATCH /api/mailing/senders/:id
 */
export const updateEmailSender = async (id, payload) =>
  safe(
    api.patch(`/senders/${id}`, payload),
    null,
    "updateEmailSender"
  );

/**
 * PATCH /api/mailing/senders/:id/set-default
 */
export const setDefaultEmailSender = async (id) =>
  safe(
    api.patch(`/senders/${id}/set-default`),
    null,
    "setDefaultEmailSender"
  );

/**
 * DELETE /api/mailing/senders/:id
 * Désactive le compte expéditeur côté serveur.
 */
export const deleteEmailSender = async (id) =>
  safe(
    api.delete(`/senders/${id}`),
    null,
    "deleteEmailSender"
  );

/* ============================================================
   CAMPAGNES
   ============================================================ */

/**
 * GET /api/mailing/campaigns
 * params possibles : { status, product_key }
 */
export const getEmailCampaigns = async (params = {}) =>
  safe(
    api.get(`/campaigns${buildQS(params)}`),
    [],
    "getEmailCampaigns"
  );

/**
 * GET /api/mailing/campaigns/stats/summary
 */
export const getEmailCampaignsSummary = async () =>
  safe(
    api.get(`/campaigns/stats/summary`),
    { total: 0, byStatus: [], byProduct: [] },
    "getEmailCampaignsSummary"
  );

/**
 * GET /api/mailing/campaigns/:id
 */
export const getEmailCampaignById = async (id) =>
  safe(
    api.get(`/campaigns/${id}`),
    null,
    "getEmailCampaignById"
  );

/**
 * POST /api/mailing/campaigns
 * payload :
 * {
 *   name,
 *   product_key,
 *   template_id,
 *   sender_account_id,
 *   from_name,
 *   from_email,
 *   reply_to,
 *   segment
 * }
 */
export const createEmailCampaign = async (payload) =>
  safe(
    api.post(`/campaigns`, payload),
    null,
    "createEmailCampaign"
  );

/**
 * PATCH /api/mailing/campaigns/:id
 */
export const updateEmailCampaign = async (id, payload) =>
  safe(
    api.patch(`/campaigns/${id}`, payload),
    null,
    "updateEmailCampaign"
  );

/**
 * DELETE /api/mailing/campaigns/:id
 */
export const deleteEmailCampaign = async (id) =>
  safe(
    api.delete(`/campaigns/${id}`),
    null,
    "deleteEmailCampaign"
  );

/**
 * POST /api/mailing/campaigns/:id/prepare
 * Prépare les destinataires selon segment_json.
 * payload possible : { limit }
 */
export const prepareEmailCampaignRecipients = async (id, payload = {}) =>
  safe(
    api.post(`/campaigns/${id}/prepare`, payload),
    null,
    "prepareEmailCampaignRecipients"
  );

/**
 * POST /api/mailing/campaigns/:id/send-test
 * payload : { to, prospection_id? }
 */
export const sendEmailCampaignTest = async (id, payload) =>
  safe(
    api.post(`/campaigns/${id}/send-test`, payload),
    null,
    "sendEmailCampaignTest"
  );

/**
 * POST /api/mailing/campaigns/:id/launch
 */
export const launchEmailCampaign = async (id) =>
  safe(
    api.post(`/campaigns/${id}/launch`),
    null,
    "launchEmailCampaign"
  );

/**
 * POST /api/mailing/campaigns/:id/pause
 */
export const pauseEmailCampaign = async (id) =>
  safe(
    api.post(`/campaigns/${id}/pause`),
    null,
    "pauseEmailCampaign"
  );

/**
 * POST /api/mailing/campaigns/:id/resume
 */
export const resumeEmailCampaign = async (id) =>
  safe(
    api.post(`/campaigns/${id}/resume`),
    null,
    "resumeEmailCampaign"
  );

/**
 * POST /api/mailing/campaigns/:id/cancel
 */
export const cancelEmailCampaign = async (id) =>
  safe(
    api.post(`/campaigns/${id}/cancel`),
    null,
    "cancelEmailCampaign"
  );

/**
 * GET /api/mailing/campaigns/:id/stats
 */
export const getEmailCampaignStats = async (id) =>
  safe(
    api.get(`/campaigns/${id}/stats`),
    null,
    "getEmailCampaignStats"
  );

/**
 * GET /api/mailing/campaigns/:id/recipients
 * params possibles : { page, limit, status }
 */
export const getEmailCampaignRecipients = async (id, params = {}) =>
  safe(
    api.get(`/campaigns/${id}/recipients${buildQS(params)}`),
    { data: [], pagination: { page: 1, limit: 0, total: 0, pages: 0 } },
    "getEmailCampaignRecipients"
  );

/**
 * GET /api/mailing/campaigns/:id/events
 * params possibles : { limit }
 */
export const getEmailCampaignEvents = async (id, params = {}) =>
  safe(
    api.get(`/campaigns/${id}/events${buildQS(params)}`),
    [],
    "getEmailCampaignEvents"
  );

/* ============================================================
   DESTINATAIRES
   ============================================================ */

/**
 * GET /api/mailing/recipients/:id
 */
export const getEmailRecipientById = async (id) =>
  safe(
    api.get(`/recipients/${id}`),
    null,
    "getEmailRecipientById"
  );

/**
 * POST /api/mailing/recipients/:id/retry
 */
export const retryEmailRecipient = async (id) =>
  safe(
    api.post(`/recipients/${id}/retry`),
    null,
    "retryEmailRecipient"
  );

/**
 * POST /api/mailing/recipients/:id/skip
 * payload : { reason }
 */
export const skipEmailRecipient = async (id, payload = {}) =>
  safe(
    api.post(`/recipients/${id}/skip`, payload),
    null,
    "skipEmailRecipient"
  );

/* ============================================================
   ALIAS COURTS OPTIONNELS
   ============================================================ */

export const getTemplates = getEmailTemplates;
export const getTemplateById = getEmailTemplateById;
export const createTemplate = createEmailTemplate;
export const updateTemplate = updateEmailTemplate;
export const deleteTemplate = deleteEmailTemplate;

export const getSenders = getEmailSenders;
export const createSender = createEmailSender;
export const updateSender = updateEmailSender;
export const deleteSender = deleteEmailSender;

export const getCampaigns = getEmailCampaigns;
export const getCampaignById = getEmailCampaignById;
export const createCampaign = createEmailCampaign;
export const updateCampaign = updateEmailCampaign;
export const deleteCampaign = deleteEmailCampaign;
export const prepareCampaignRecipients = prepareEmailCampaignRecipients;
export const sendCampaignTest = sendEmailCampaignTest;
export const launchCampaign = launchEmailCampaign;
export const pauseCampaign = pauseEmailCampaign;
export const resumeCampaign = resumeEmailCampaign;
export const cancelCampaign = cancelEmailCampaign;
export const getCampaignStats = getEmailCampaignStats;
export const getCampaignRecipients = getEmailCampaignRecipients;
export const getCampaignEvents = getEmailCampaignEvents;